import { useState, useCallback, useEffect, useMemo, type ComponentType } from "react";
import { isAuthenticated, validateSession, clearToken } from "./auth";
import LoginPage from "./components/LoginPage";
import ProductLedHealth from "./components/ProductLedHealth";
import { ADMIN_TAB_VISIBILITY } from "./components/plh/plhTileInfo";
import "./components/plh/plh-kpi.css";

type Theme = "light" | "dark";

interface TabDef {
  id: string;
  label: string;
  component: ComponentType<{ parentTheme: Theme; onThemeToggle?: () => void }>;
}

const TAB_REGISTRY: TabDef[] = [
  { id: "support", label: "Support", component: () => <div style={{ padding: 32 }}>Support — coming soon.</div> },
  { id: "product-led-health", label: "Product-Led Health", component: ProductLedHealth },
];

function getStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem("plh-theme");
    if (stored === "dark" || stored === "light") return stored;
  } catch { /* ignore */ }
  return "light";
}

export default function App() {
  const [theme, setTheme] = useState<Theme>(getStoredTheme);
  const [authed, setAuthed] = useState(isAuthenticated);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    if (!isAuthenticated()) {
      setAuthChecked(true);
      return;
    }

    let cancelled = false;
    validateSession().then((result) => {
      if (cancelled) return;
      if (!result.authenticated) {
        clearToken();
        setAuthed(false);
      }
      setAuthChecked(true);
    });
    return () => { cancelled = true; };
  }, []);

  const visibleTabs = useMemo(
    () => TAB_REGISTRY.filter((t) => ADMIN_TAB_VISIBILITY[t.id] !== false),
    [],
  );

  const [activeTabId, setActiveTabId] = useState(() => visibleTabs[0]?.id ?? "product-led-health");

  const handleThemeToggle = useCallback(() => {
    setTheme((prev) => {
      const next = prev === "light" ? "dark" : "light";
      try { localStorage.setItem("plh-theme", next); } catch { /* ignore */ }
      return next;
    });
  }, []);

  if (!authed && !authChecked) {
    return (
      <div className="plh-dashboard" data-theme={theme} style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
        <div style={{ textAlign: "center", color: "var(--t2, #555)" }}>Checking session…</div>
      </div>
    );
  }

  if (!authed) {
    return <LoginPage onLogin={() => setAuthed(true)} />;
  }

  const activeTab = visibleTabs.find((t) => t.id === activeTabId) ?? visibleTabs[0];
  const ActiveComponent = activeTab?.component;

  return (
    <div className="plh-dashboard" data-theme={theme}>
      {visibleTabs.length > 0 && (
        <div className="admin-tab-bar">
          <div className="admin-tab-links">
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`admin-tab-link${tab.id === activeTab?.id ? " active" : ""}`}
                onClick={() => setActiveTabId(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="admin-tab-right">
            <button
              type="button"
              className="theme-btn"
              onClick={handleThemeToggle}
              aria-label={`Switch to ${theme === "light" ? "dark" : "light"} mode`}
            >
              {theme === "light" ? "☽" : "☀"}
            </button>
            <button
              type="button"
              className="plh-text-btn admin-sign-out-btn"
              onClick={() => { clearToken(); setAuthed(false); }}
              aria-label="Sign out"
            >
              Sign out
            </button>
          </div>
        </div>
      )}

      {ActiveComponent && <ActiveComponent parentTheme={theme} />}
    </div>
  );
}
